import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './TopNavbar.css';

function normalizeHeight(height) {
  if (typeof height === 'number') return `${height}px`;
  if (typeof height === 'string' && height.trim().length > 0) return height.trim();
  return '100px';
}

/**
 * Reusable fixed top navbar.
 *
 * Props:
 * - items: [{ id: string, label: string }]
 * - logo: ReactNode
 * - activeId: string (controlled)
 * - defaultActiveId: string (uncontrolled)
 * - onSelect: (id: string) => void
 * - height: number|string (default 100px)
 */
export default function TopNavbar({
  items,
  logo,
  activeId: activeIdProp,
  defaultActiveId,
  onSelect,
  height = 100,
}) {
  const isControlled = typeof activeIdProp === 'string';
  const [activeIdState, setActiveIdState] = useState(
    defaultActiveId ?? items?.[0]?.id ?? ''
  );
  const activeId = isControlled ? activeIdProp : activeIdState;
  const activeIdRef = useRef(activeIdState);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const panelRef = useRef(null);
  const isNavigatingRef = useRef(false);
  const navigationTimerRef = useRef(0);
  const sectionsRef = useRef([]);

  const heightCss = useMemo(() => normalizeHeight(height), [height]);

  useEffect(() => {
    activeIdRef.current = activeIdState;
  }, [activeIdState]);

  const scheduleNavigationUnlock = useCallback(() => {
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
    }
    navigationTimerRef.current = window.setTimeout(() => {
      isNavigatingRef.current = false;
    }, 160);
  }, []);

  const startNavigation = useCallback(() => {
    isNavigatingRef.current = true;
    scheduleNavigationUnlock();
  }, [scheduleNavigationUnlock]);

  useEffect(() => () => {
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
    }
  }, []);

  useEffect(() => {
    sectionsRef.current = [];
  }, [items]);

  function setUrlHash(nextId, mode) {
    if (!nextId) return;
    const nextHash = `#${nextId}`;
    if (window.location.hash === nextHash) return;

    try {
      if (mode === 'push') window.history.pushState(null, '', nextHash);
      else window.history.replaceState(null, '', nextHash);
    } catch {
      // Fallback for environments that restrict history manipulation.
      window.location.hash = nextHash;
    }
  }

  // Close the mobile menu on escape and on outside click.
  useEffect(() => {
    if (!isMenuOpen) return;

    function onKeyDown(e) {
      if (e.key === 'Escape') setIsMenuOpen(false);
    }

    function onPointerDown(e) {
      const panel = panelRef.current;
      if (!panel) return;
      if (panel.contains(e.target)) return;
      // Click outside the open panel closes it.
      setIsMenuOpen(false);
    }

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [isMenuOpen]);

  // Sync scroll position from the current URL hash (deep links + back/forward).
  useEffect(() => {
    if (!Array.isArray(items) || items.length === 0) return;

    function scrollToHashTarget() {
      // Skip state updates during programmatic navigation to prevent flickering
      if (isNavigatingRef.current) return;

      const raw = window.location.hash || '';
      const id = raw.startsWith('#') ? raw.slice(1) : raw;
      if (!id) return;
      if (!items.some((it) => it.id === id)) return;

      if (!isControlled) setActiveIdState(id);
      startNavigation();
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // On mount, respect the existing hash.
    scrollToHashTarget();

    // hashchange: user edits URL / anchor navigation
    // popstate: back/forward when we use pushState/replaceState
    window.addEventListener('hashchange', scrollToHashTarget);
    window.addEventListener('popstate', scrollToHashTarget);
    return () => {
      window.removeEventListener('hashchange', scrollToHashTarget);
      window.removeEventListener('popstate', scrollToHashTarget);
    };
  }, [isControlled, items, startNavigation]);

  // Keep active item in sync with scroll position (best-effort).
  useEffect(() => {
    if (isControlled) return;
    if (!Array.isArray(items) || items.length === 0) return;

    let rafId = 0;

    const getOffset = () => {
      const parsed = parseFloat(heightCss);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const updateActive = () => {
      if (isNavigatingRef.current) {
        scheduleNavigationUnlock();
        return;
      }

      const sections = sectionsRef.current.length
        ? sectionsRef.current
        : items.map((it) => document.getElementById(it.id)).filter(Boolean);

      sectionsRef.current = sections;

      if (sections.length === 0) return;

      const offset = getOffset();
      let nextId = '';

      const containing = sections.find((section) => {
        const rect = section.getBoundingClientRect();
        return rect.top <= offset && rect.bottom > offset;
      });

      if (containing) {
        nextId = containing.id;
      } else {
        let bestDistance = Number.POSITIVE_INFINITY;
        sections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          if (rect.bottom <= offset) return;
          const distance = Math.abs(rect.top - offset);
          if (distance < bestDistance) {
            bestDistance = distance;
            nextId = section.id;
          }
        });
      }

      if (!nextId && sections.length) {
        nextId = sections[sections.length - 1].id;
      }

      if (nextId && nextId !== activeIdRef.current) {
        setActiveIdState(nextId);
        setUrlHash(nextId, 'replace');
      }
    };

    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateActive);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [heightCss, isControlled, items, scheduleNavigationUnlock]);

  function selectItem(id) {
    if (!isControlled) setActiveIdState(id);
    onSelect?.(id);
    setIsMenuOpen(false);

    // Mark as navigating to prevent IntersectionObserver and hashchange conflicts
    startNavigation();

    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      isNavigatingRef.current = false;
    }

    // On click, treat it like navigation (add to history).
    setUrlHash(id, 'push');
  }

  const leftRef = useRef(null);

  useEffect(() => {
    if (!leftRef.current) return;
    const leftEl = leftRef.current;
    const leftStyles = window.getComputedStyle(leftEl);
    const leftRect = leftEl.getBoundingClientRect();

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/3d701484-8f96-4e8a-9757-500629c52a67', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'TopNavbar.js:177', message: 'Left container styles', data: { width: leftStyles.width, height: leftStyles.height, overflow: leftStyles.overflow, position: leftStyles.position, minWidth: leftStyles.minWidth, rectWidth: leftRect.width, rectHeight: leftRect.height, rectTop: leftRect.top, rectLeft: leftRect.left }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'run1', hypothesisId: 'A' }) }).catch(() => { });
    // #endregion agent log

    // Check pseudo-element via CSS (we can't directly access it, but we can check parent)
    const navbarEl = leftEl.closest('.TopNavbar');
    const navbarRect = navbarEl?.getBoundingClientRect();

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/3d701484-8f96-4e8a-9757-500629c52a67', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'TopNavbar.js:185', message: 'Navbar container check', data: { navbarHeight: heightCss, navbarRectHeight: navbarRect?.height, navbarRectTop: navbarRect?.top, leftTopRelative: leftRect.top - navbarRect.top }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'run1', hypothesisId: 'B' }) }).catch(() => { });
    // #endregion agent log

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/3d701484-8f96-4e8a-9757-500629c52a67', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'TopNavbar.js:190', message: 'CSS variable check', data: { cssVarHeight: getComputedStyle(document.documentElement).getPropertyValue('--navbar-height') || heightCss }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'run1', hypothesisId: 'C' }) }).catch(() => { });
    // #endregion agent log

    // Check pseudo-element styles via CSS
    const beforeStyles = window.getComputedStyle(leftEl, '::before');

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/3d701484-8f96-4e8a-9757-500629c52a67', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'TopNavbar.js:198', message: 'Pseudo-element gradient styles', data: { beforeWidth: beforeStyles.width, beforeHeight: beforeStyles.height, beforeTop: beforeStyles.top, beforeLeft: beforeStyles.left, beforeTransform: beforeStyles.transform, beforeBackground: beforeStyles.background, beforePosition: beforeStyles.position }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'post-fix', hypothesisId: 'D' }) }).catch(() => { });
    // #endregion agent log

    // Calculate expected gradient dimensions
    const containerWidth = parseFloat(leftStyles.width) || 140;
    const expectedGradientWidth = Math.min(300, containerWidth * 2);

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/3d701484-8f96-4e8a-9757-500629c52a67', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ location: 'TopNavbar.js:207', message: 'Gradient dimension analysis', data: { containerWidth: containerWidth, expectedGradientWidth: expectedGradientWidth, actualGradientWidth: parseFloat(beforeStyles.width), navbarHeight: parseFloat(heightCss), actualGradientHeight: parseFloat(beforeStyles.height) }, timestamp: Date.now(), sessionId: 'debug-session', runId: 'post-fix', hypothesisId: 'E' }) }).catch(() => { });
    // #endregion agent log
  }, [heightCss]);

  return (
    <header className="TopNavbar" style={{ '--navbar-height': heightCss }}>
      <nav className="TopNavbar__inner" aria-label="Primary navigation">
        <div className="TopNavbar__left" ref={leftRef}>
          <div className="App-logoMark">
            <span className="App-logoMark__primary">PRESIDIO</span>
            <span className="App-logoMark__secondary">DIGITAL</span>
          </div>
        </div>

        <div className="TopNavbar__right">
          <button
            type="button"
            className="TopNavbar__menuButton"
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            <span className="TopNavbar__menuIcon" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>

          <ul className="TopNavbar__items">
            {items.map((item) => {
              const isActive = item.id === activeId;
              return (
                <li key={item.id} className="TopNavbar__item">
                  <button
                    type="button"
                    className={`TopNavbar__link ${isActive ? 'is-active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => selectItem(item.id)}
                  >
                    <span className="TopNavbar__label">{item.label}</span>
                    <span className="TopNavbar__dot GradientDot" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>

          <div
            ref={panelRef}
            className={`TopNavbar__mobilePanel ${isMenuOpen ? 'is-open' : ''}`}
            role="menu"
            aria-label="Mobile navigation"
          >
            <ul className="TopNavbar__mobileItems">
              {items.map((item) => {
                const isActive = item.id === activeId;
                return (
                  <li key={item.id} className="TopNavbar__mobileItem" role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className={`TopNavbar__mobileLink ${isActive ? 'is-active' : ''}`}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => selectItem(item.id)}
                    >
                      <span className="TopNavbar__label">{item.label}</span>
                      <span className="TopNavbar__dot GradientDot" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
}

